import type { PageBackgroundImages, ResponsiveImage } from '@lemonade-hq/uniclient-chat';
import { Flex, Layout, useThemeImage, useViewportSize } from '@lemonade-hq/uniclient-theme';
import { useCallback, useMemo } from 'react';
import * as styles from './SpreadBackground.css';

interface BackgroundImageProps {
  readonly image: string;
  readonly position: 'left' | 'right';
  readonly layout: 'dynamic' | 'fixed';
}

const BackgroundImage: React.FC<BackgroundImageProps> = ({ image, position, layout }) => {
  const imageUrl = useThemeImage(image);
  return (
    <Layout
      className={styles.backgroundImage({ position, layout })}
      role="presentation"
      style={{ backgroundImage: `url(${imageUrl})` }}
    />
  );
};

type Viewport = 'lg' | 'md' | 'sm';

export const getResponsiveBreakpoint = (isDesktop: boolean, isTablet: boolean): Viewport => {
  if (isDesktop) return 'lg';

  return isTablet ? 'md' : 'sm';
};

interface BackgroundProps {
  readonly images: NonNullable<PageBackgroundImages>;
  readonly cardHeight: number;
}

export const SpreadBackground: React.FC<BackgroundProps> = ({ images, cardHeight }) => {
  const { isAboveDesktopViewport, isTabletViewport } = useViewportSize();

  const getImage = useCallback(
    (image: ResponsiveImage | string): string => {
      const viewport = getResponsiveBreakpoint(isAboveDesktopViewport, isTabletViewport);

      if (typeof image === 'string') return image;

      return image[viewport] ?? image.sm;
    },
    [isAboveDesktopViewport, isTabletViewport],
  );

  const layout = typeof images.left === 'string' ? 'dynamic' : 'fixed';
  const imageLeft = useMemo(() => getImage(images.left), [images.left, getImage]);
  const imageRight = useMemo(() => getImage(images.right), [images.right, getImage]);

  const gap = useMemo(() => {
    if (typeof images.gap === 'number') return images.gap;

    const gaps = images.gap ?? styles.SPACER_GAP;
    return isAboveDesktopViewport ? gaps.lg : isTabletViewport ? gaps.md : gaps.sm;
  }, [isAboveDesktopViewport, isTabletViewport, images.gap]);

  return (
    <Layout
      className={styles.mainBg({ layout })}
      left="50%"
      position="absolute"
      top={
        layout === 'fixed'
          ? 0
          : {
              sm: '-50px',
              lg: `calc((${cardHeight}px + ${styles.SPACER_BETWEEN_CARD_AND_FLOOR}) - (${styles.BACKGROUND_IMAGE_HEIGHT.lg} - ${styles.BACKGROUND_FLOOR_HEIGHT.lg}))`,
            }
      }
      width="100vw"
    >
      <Flex
        alignItems={layout === 'dynamic' ? 'flex-start' : 'flex-end'}
        flexDirection="row"
        height="100%"
        position="absolute"
        width="100%"
      >
        <BackgroundImage image={imageLeft} layout={layout} position="left" />
        <Flex className={styles.backgroundSpacer({ layout })} flexBasis={`${gap}px`} flexGrow={1} flexShrink={0} />
        <BackgroundImage image={imageRight} layout={layout} position="right" />
        <Layout
          backgroundColor="$backgroundSecondary"
          bottom="0"
          className={styles.floor({ layout })}
          position="absolute"
          width="100%"
        />
      </Flex>
    </Layout>
  );
};
